import { Box, Flex, LegacyText, TransparentButton } from "flicket-ui";
import { useContext, useEffect } from "react";
import { Status } from "~components";
import { Layout } from "~components/common/Layout";
import { BaseContext } from "~context";
import { useActiveCompetitions } from "~features/events/hooks/useActiveCompetitions";
import useDetailsPageContext, {
  competitionFromPageContext,
  isRegistrationPageContext,
} from "~features/events/hooks/useDetailsPageContext";
import useShowReleaseCodes from "~features/events/hooks/useShowReleaseCodes";
import { GetEventAndReleaseForCustomerQuery } from "~graphql/sdk";
import usePageLoad from "~telemetry/traces/usePageLoad";
import MainCTA from "../events.MainCTA";
import ReleaseCodesModal from "../ReleaseCodesModal";
import { Desktop } from "./detail.Desktop";
import { Mobile } from "./detail.Mobile";

import { useSeekaConverge } from "@seeka-labs/converge-react";
import Router, { useRouter } from "next/router";
import EventHeader from "~components/common/common.EventHeader";
import { useIsMobile, useOrganization } from "~hooks";
import { useGoogleAnalyticsPageView } from "~hooks/useGoogleAnalyticsPageView";
import { AttributeNames, PageName } from "~telemetry/enums/AttributeNames";
import CustomModal from "~components/CustomModal";
import { RegistrationModalContent } from "../RegistrationModalContent";
import { RegistrationSuccessModalContent } from "../RegistrationSuccessModalContent";

export type DetailsProps = {
  event: GetEventAndReleaseForCustomerQuery["getEventAndReleaseForCustomer"]["event"];
  release?: GetEventAndReleaseForCustomerQuery["getEventAndReleaseForCustomer"]["release"];
};

export const Detail = ({ event, release }: DetailsProps) => {
  const { activeCompetitions, isLoading } = useActiveCompetitions(event.id);
  const pageContext = useDetailsPageContext(event, release, activeCompetitions);
  const { setActiveModal, activeModal, state } = pageContext;
  const isMobile = useIsMobile();
  const converge = useSeekaConverge();
  const router = useRouter();
  const { hasFeature } = useOrganization();

  const { isSinglePage, organization } = useContext(BaseContext);
  const showReleaseCodes = useShowReleaseCodes(event.enableReleaseCodes);

  const competitionId = competitionFromPageContext(pageContext)?.id;

  const footerHeight = showReleaseCodes ? "110px" : "70px";

  useGoogleAnalyticsPageView(
    !!event?.id,
    PageName.EVENT_DETAILS_PAGE,
    event?.id
  );

  usePageLoad(
    PageName.EVENT_DETAILS_PAGE,
    {
      forwardToNest: true,
      eventId: event.id,
      attributes: {
        [AttributeNames.FLICKET_EVENT_DETAILS_PAGE_STATE]: state,
        [AttributeNames.FLICKET_COMPETITION_ID]: competitionId,
      },
    },
    // Only fire the event after the active competitions have finished loading so that the pageContext is correct
    !isLoading
  );

  useEffect(() => {
    sessionStorage.removeItem("accessCodeId");
    void converge?.track?.viewProduct({
      productIdentifier: event?.id,
      productName: event?.title,
    });
  }, []);

  const preAuthEnabled = hasFeature("prePurchaseRegistration");

  useEffect(() => {
    const modal = router.query.modal;
    if (
      modal === "registration" &&
      isRegistrationPageContext(pageContext) &&
      preAuthEnabled
    ) {
      pageContext.setActiveModal("registration");
    }
    // This should only run once when the page first loads.
    // preAuthEnabled is included in the dependencies for debugging,
    // since the feature flag can be changed from the side panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preAuthEnabled]);

  const showMMobileEventHeader = !(isSinglePage && Router.pathname === "/");

  return (
    <Status loading={isLoading} error={false}>
      {/* Mobile */}
      {isMobile && (
        <Box d={{ _: "flex", md: "none" }} flexDirection="column" flex={1}>
          {showMMobileEventHeader && (
            <EventHeader
              type="event"
              title={event.title}
              startDate={event?.startDate}
              endDate={event?.endDate}
              venue={event?.venue}
              goBack={() => void Router.push("/")}
              accessibility={event?.accessibility}
              supportInfo={organization?.supportInfo}
              image={event?.logo}
              description={event?.description}
              coordinates={`${event?.venue?.location?.lat},${event?.venue?.location?.lng}`}
            />
          )}
          <Box
            display={"flex"}
            flex={1}
            flex-direction={"column"}
            position={"relative"}
            width={"100%"}
            maxWidth={"100vw"}
            pt={6}
            paddingBottom={footerHeight as any}
          >
            <Mobile pageContext={pageContext} />
          </Box>
          <Flex
            height={footerHeight}
            flexDirection={"column"}
            justifyContent={"center"}
            position={"fixed"}
            bottom={0}
            left={0}
            right={0}
            px={2}
            py={1}
            background="white"
            borderTop={"1px solid"}
            borderColor={"N200"}
          >
            <MainCTA pageContext={pageContext} />
            {showReleaseCodes && (
              <TransparentButton onClick={() => setActiveModal("releaseCodes")}>
                <LegacyText
                  fontWeight={"extraBold"}
                  fontSize={3}
                  color="N800"
                  my={"6/4"}
                >
                  Have an access code?
                </LegacyText>
              </TransparentButton>
            )}
          </Flex>
        </Box>
      )}
      {/* Desktop */}
      {!isMobile && (
        <Box d={{ _: "none", md: "flex" }} flexDirection="column" flex={1}>
          <Layout layoutType="main">
            <Desktop pageContext={pageContext} isSinglePage={isSinglePage} />
          </Layout>
        </Box>
      )}

      <ReleaseCodesModal
        isOpen={activeModal === "releaseCodes"}
        onClose={() => setActiveModal(undefined)}
        event={event}
      />
      <CustomModal
        isOpen={activeModal === "registration"}
        close={() => setActiveModal(undefined)}
      >
        <RegistrationModalContent pageContext={pageContext} />
      </CustomModal>
      <CustomModal
        isOpen={activeModal === "registration-success"}
        close={() => setActiveModal(undefined)}
      >
        <RegistrationSuccessModalContent
          key={activeModal}
          pageContext={pageContext}
        />
      </CustomModal>
    </Status>
  );
};
